import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import PopupRenameFile from '../../../components/PopupRenameFile'
import {
  FormActionFooter,
  UploadFileListItem,
  UploadFileListWrapper,
} from './BoardReportUpdateModalStyled'
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Tabs,
  Tooltip,
  Upload,
} from 'antd'
import {
  DeleteOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { inject, observer } from 'mobx-react'
import validator from '../../../validator'
import utils from '../../../utils'
import RichEditor from '../../../components/RichEditor/RichEditor'
import DraggerUploadButton from '../../../components/Common/DraggerUploadButton'

const { TextArea } = Input

const BoardReportUpdateModal = props => {
  const {
    boardReportStore,
    selectedBoardReport,
    fileStore,
    loadingAnimationStore,
    visible,
    onCancel,
  } = props

  const [form] = Form.useForm()
  const [fileExt, setFileExt] = useState(undefined)
  const [fileTargetRename, setFileTargetRename] = useState(null)
  const [isRenameFileUpdate, setIsRenameFileUpdate] = useState(false)
  const [isModalVisibleRenameFile, setIsModalVisibleRenameFile] = useState(
    false
  )
  const [fileList, setFileList] = useState([])
  const [existingFiles, setExistingFiles] = useState([])
  const EDITOR_REF = useRef()
  const [editorContent, setEditorContent] = useState('')

  const handleEditorChange = content => {
    setEditorContent(content)
    form.setFieldsValue({ content: content })
  }

  const handleChangeFile = info => {
    if (info.fileList.length === 0) {
      form.setFieldsValue({ file_upload: undefined })
      setFileList([])
      return
    }

    const newFileList = []

    info.fileList.forEach(file => {
      const fileNameLower = file.name.toLowerCase()

      const isDuplicateInNew = newFileList.some(
        f => f.name.toLowerCase() === fileNameLower
      )

      const isDuplicateInExisting = existingFiles.some(
        f => f.fileName.toLowerCase() === fileNameLower
      )

      if (isDuplicateInNew || isDuplicateInExisting) {
        message.warning(`File đã có trong danh sách`)
      } else {
        newFileList.push(file)
      }
    })

    setFileList(newFileList)

    validateAttachments(newFileList, existingFiles)
  }

  const handleRemoveFileFromUploadList = useCallback(
    fileUID => {
      const newFileList = fileList.filter(file => file.uid !== fileUID)
      setFileList(newFileList)
      validateAttachments(newFileList, existingFiles)
    },
    [fileList, existingFiles]
  )

  const handleRemoveExistingFile = file_id => {
    const newExistingFiles = existingFiles.filter(file => file.id !== file_id)
    setExistingFiles(newExistingFiles)
    validateAttachments(fileList, newExistingFiles)
  }

  const validateAttachments = (newFiles, existingFiles) => {
    const totalFiles = newFiles.length + existingFiles.length
    if (totalFiles === 0) {
      form.setFieldsValue({ attachments: undefined })
    } else {
      form.setFieldsValue({ attachments: 'has_files' })
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setFileList([])
    setExistingFiles([])
    onCancel()
  }

  const handleUpdateBoardReport = async submitData => {
    await boardReportStore.updateBoardReport(
      selectedBoardReport.code,
      submitData
    )
    message.success('Cập nhật báo cáo thành công')
  }

  const onFinish = async values => {
    if (!selectedBoardReport) {
      message.error('Không tìm thấy dữ liệu báo cáo!')
      loadingAnimationStore.showSpinner(false)
      return
    }

    loadingAnimationStore.showSpinner(true)
    const html = EDITOR_REF.current?.editor.getData() || ''

    const { title } = values
    const batchUploadList = []
    fileList.forEach(file => {
      if (file.originFileObj) {
        const formData = new FormData()
        formData.append('file', file.originFileObj, file.name)
        batchUploadList.push(fileStore.uploadFile(formData))
      }
    })

    try {
      const response = await Promise.all(batchUploadList)
      const new_items = []
      const remove_items = []

      const originalFileIds = (selectedBoardReport?.files || []).map(
        file => file.fileId
      )
      const currentExistingFileIds = existingFiles.map(file => file.fileId)
      const newUploadedFileIds = response.map(res => res.data.file_id)
      const currentAllFileIds = [
        ...currentExistingFileIds,
        ...newUploadedFileIds,
      ]

      newUploadedFileIds.forEach(fileId => {
        if (!originalFileIds.includes(fileId)) {
          new_items.push(fileId)
        }
      })

      originalFileIds.forEach(fileId => {
        if (!currentAllFileIds.includes(fileId)) {
          remove_items.push(fileId)
        }
      })

      const isTitleChanged = title !== selectedBoardReport.title
      const isContentChanged = html !== selectedBoardReport.content
      const isRenamed = (selectedBoardReport.files || []).some(oldFile => {
        const currentFile = existingFiles.find(f => f.fileId === oldFile.fileId)
        return currentFile && currentFile.fileName !== oldFile.fileName
      })
      const isAttachmentChanged =
        new_items.length > 0 || remove_items.length > 0 || isRenamed

      if (!isTitleChanged && !isContentChanged && !isAttachmentChanged) {
        message.info('Không có giá trị thay đổi')
        loadingAnimationStore.showSpinner(false)
        return
      }

      const submitValues = {
        title,
        content: html,
        attachments: {
          remove_items,
          new_items,
        },
      }

      await handleUpdateBoardReport(submitValues)
      await boardReportStore.getBoardReportByCode(selectedBoardReport.code)
      handleCancel()
    } catch (error) {
      console.error(error)
      message.error(error.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const renameFile = (originalFile, newName) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    })
  }

  const submitRenameFile = async value => {
    if (!fileTargetRename || fileTargetRename.index === undefined) {
      message.error('Không thể đổi tên file: File không hợp lệ')
      setIsModalVisibleRenameFile(false)
      return
    }

    const index = fileTargetRename.index
    const trimmedName = value.rename.trim()
    const newFileName = trimmedName + fileExt

    if (!trimmedName) {
      message.warning('Tên file không được để trống')
      return
    }

    const existingNames = [
      ...existingFiles
        .filter((f, i) => !(isRenameFileUpdate && i === index))
        .map(f => f.fileName.toLowerCase()),
      ...fileList
        .filter((f, i) => !(!isRenameFileUpdate && i === index))
        .map(f => f.name.toLowerCase()),
    ]

    if (existingNames.includes(newFileName.toLowerCase())) {
      message.warning('Tên file đã tồn tại, vui lòng chọn tên khác')
      return
    }

    if (isRenameFileUpdate) {
      try {
        await fileStore.handleRenameFile(fileTargetRename.fileId, newFileName)
        const updatedExistingFiles = [...existingFiles]
        updatedExistingFiles[index].fileName = newFileName
        updatedExistingFiles[index].name = newFileName
        setExistingFiles(updatedExistingFiles)
        message.success('Đổi tên file thành công')
      } catch (error) {
        console.error('Rename file error:', error)
        message.error('Đổi tên file thất bại')
      }
    } else {
      const newFileList = [...fileList]
      newFileList[index].name = newFileName
      if (fileTargetRename.originFileObj) {
        newFileList[index].originFileObj = renameFile(
          newFileList[index].originFileObj,
          newFileName
        )
      }
      setFileList(newFileList)
      message.success('Đổi tên file thành công')
    }

    setIsModalVisibleRenameFile(false)
  }

  // Initialize form with selectedBoardReport data
  useEffect(() => {
    if (visible && selectedBoardReport) {
      form.setFieldsValue({
        title: selectedBoardReport.title || '',
        content: selectedBoardReport.content || '',
      })
      setEditorContent(selectedBoardReport.content || '')

      // Initialize existing files
      if (selectedBoardReport.files && selectedBoardReport.files.length > 0) {
        const formattedFiles = selectedBoardReport.files.map(file => ({
          uid: file.id.toString(),
          id: file.id,
          fileId: file.fileId,
          name: file.fileName,
          fileName: file.fileName,
          status: 'done',
          fileType: file.fileType,
        }))

        // Remove duplicates based on id
        const uniqueFiles = formattedFiles.filter(
          (file, index, self) => index === self.findIndex(f => f.id === file.id)
        )

        setExistingFiles(uniqueFiles)
        setFileList([]) // Clear new files

        // Set form field to indicate there are attachments
        form.setFieldsValue({ attachments: 'has_files' })
      } else {
        setExistingFiles([])
        setFileList([])
        form.setFieldsValue({ attachments: undefined })
      }
    }
  }, [visible, selectedBoardReport, form])

  const [fieldsRenameFile, setFieldsRenameFile] = useState([
    {
      name: ['rename'],
      value: '',
    },
  ])

  const handleOpenPopupRenameFile = (file, index) => {
    setFieldsRenameFile([
      {
        name: ['rename'],
        value: utils.removeExtensionFile(file.name || file.fileName),
      },
    ])
    setFileExt('.' + utils.getExtensionFile(file.name || file.fileName))
    setFileTargetRename({ ...file, index })
    setIsModalVisibleRenameFile(true)
  }

  // Render existing files
  const renderExistingFiles = existingFiles.map((file, index) => (
    <UploadFileListItem key={`existing-${file.id}`}>
      <FileTextOutlined style={{ color: '#1890FF' }} />
      <span style={{ color: '#1890FF' }}>{file.fileName}</span>
      <Button
        className="rename"
        type="text"
        onClick={() => {
          setIsRenameFileUpdate(true)
          handleOpenPopupRenameFile(file, index)
        }}>
        Đổi tên
      </Button>
      <Tooltip title="Xoá tập tin">
        <DeleteOutlined onClick={() => handleRemoveExistingFile(file.id)} />
      </Tooltip>
    </UploadFileListItem>
  ))

  // Render new uploaded files
  const renderNewFiles = fileList.map((file, index) => (
    <UploadFileListItem key={`new-${file.uid}`}>
      <FileTextOutlined />
      <span>{file.name}</span>
      <Button
        className="rename"
        type="text"
        onClick={() => {
          setIsRenameFileUpdate(false)
          handleOpenPopupRenameFile(file, index)
        }}>
        Đổi tên
      </Button>
      <Tooltip title="Xoá tập tin">
        <DeleteOutlined
          onClick={() => handleRemoveFileFromUploadList(file.uid)}
        />
      </Tooltip>
    </UploadFileListItem>
  ))

  return (
    <Modal
      visible={visible}
      title="Chỉnh sửa báo cáo"
      onCancel={handleCancel}
      width={650}
      wrapClassName="custom-modal-wrapper"
      footer={null}
      destroyOnClose>
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
        style={{ paddingTop: 10 }}>
        <Form.Item
          name="title"
          label={<span>Tiêu đề</span>}
          rules={[
            { required: true, message: 'Vui lòng nhập tiêu đề' },
            { validator: validator.validateInputString },
          ]}>
          <Input maxLength={200} placeholder="Nhập tiêu đề báo cáo" />
        </Form.Item>

        <Tabs
          defaultActiveKey="1"
          destroyInactiveTabPane={false}
          style={{
            marginTop: 20,
          }}>
          <Tabs.TabPane tab="Nội dung" key="1" forceRender>
            <Form.Item name="content" label={<span>Nội dung</span>}>
              <RichEditor
                EDITOR_REF={EDITOR_REF}
                placeholder="Nhập nội dung báo cáo"
                onEditorChange={handleEditorChange}
                editorContent={editorContent}
              />
            </Form.Item>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Tài liệu đính kèm" key="2" forceRender>
            <Form.Item
              name="attachments"
              label={<span>Tài liệu đính kèm</span>}>
              <Upload.Dragger
                fileList={fileList}
                multiple
                beforeUpload={() => false}
                onChange={handleChangeFile}
                showUploadList={false}
                className="custom-dragger-upload bg-white">
                <DraggerUploadButton />
              </Upload.Dragger>
            </Form.Item>

            <UploadFileListWrapper>
              {renderExistingFiles}
              {renderNewFiles}
            </UploadFileListWrapper>
          </Tabs.TabPane>
        </Tabs>

        <div className="custom-create-page-footer-sticky">
          <Button
            danger
            className="custom-ant-button w-160"
            htmlType={'button'}
            onClick={handleCancel}>
            Huỷ bỏ
          </Button>

          <Button
            type={'primary'}
            className="custom-ant-button w-160"
            htmlType={'submit'}>
            Cập nhật
          </Button>
        </div>
      </Form>

      <PopupRenameFile
        isModalVisibleRenameFile={isModalVisibleRenameFile}
        handleCloseModalRenameFile={() => setIsModalVisibleRenameFile(false)}
        submitRenameFile={submitRenameFile}
        fieldsRenameFile={fieldsRenameFile}
        fileExt={fileExt}
      />
    </Modal>
  )
}

BoardReportUpdateModal.propTypes = {}

export default memo(
  inject(
    'boardReportStore',
    'fileStore',
    'loadingAnimationStore'
  )(observer(BoardReportUpdateModal))
)
